import {
  PhoneIcon,
  VideoCameraIcon,
  MapPinIcon
} from "@heroicons/react/24/outline";
import { capitalize } from "../libraries/nodejs-manager/src/libraries/format/string";
import {
  candidateAdvanceEntity,
  candidateEntity,
  jobCandidateArchiveEntity,
  jobCandidateOfferAcceptEntity,
  jobCandidateOfferDeclineEntity,
  jobCandidateOfferEntity,
  jobDescriptionSectionEntity,
  jobRoleWorkflowQuestionEntity,
  jobRoleWorkflowStepEntity
} from "./entities";
import { ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN } from "./roles";
import {
  AtSymbolIcon,
  CheckCircleIcon,
  ChevronDoubleDownIcon,
  DevicePhoneMobileIcon,
  DocumentIcon,
  FaceSmileIcon,
  GiftIcon,
  HandThumbUpIcon,
  PaperAirplaneIcon,
  PencilIcon,
  Square2StackIcon,
  StopCircleIcon
} from "@heroicons/react/20/solid";

export const QUESTION_SCALE_RANGE = "range";
export const QUESTION_SCALE_PASS_FAIL = "pass_fail";
export const QUESTION_SCALE_SCORE = "score";

export const QUESTION_SCALE = [
  {
    label: "Scale",
    value: QUESTION_SCALE_RANGE
  },
  { label: "Pass/Fail", value: QUESTION_SCALE_PASS_FAIL },
  { label: "Numeric Input (0-100)", value: QUESTION_SCALE_SCORE }
];

export const NOTIFY_MENTIONED_USERS = "NOTIFY_MENTIONED_USERS";
export const NOTIFY_ASSIGNED_REVIEWERS = "NOTIFY_ASSIGNED_REVIEWERS";
export const NOTIFY_CONTENT_REVIEW = "NOTIFY_CONTENT_REVIEW";

export const CONTENT_REVIEW_APPROVED = "approved";
export const CONTENT_REVIEW_PENDING = "pending";
export const CONTENT_REVIEW_ACTIVE = "active";

export const CONTENT_REVIEW_RESPONSE_APPROVE = "approve";
export const CONTENT_REVIEW_RESPONSE_REQUEST = "request";
export const CONTENT_REVIEW_RESPONSE_COMMENT = "comment";
export const CONTENT_REVIEW_RESPONSE_CHANGE = "change";

export const CONTENT_REVIEW_ASSIGNEE_OWNERS = "owners";
export const CONTENT_REVIEW_ASSIGNEE_REVIEWERS = "reviewers";

export const CONTENT_FEEDBACK_SUPPORT = "support";
export const CONTENT_FEEDBACK_ACKNOWLEDGE = "acknowledge";
export const CONTENT_FEEDBACK_DONE = "done";

export const CONTENT_REVIEW_MODE_JOB = "jobdesc";
export const CONTENT_REVIEW_MODE_QUESTION = "question";

export const INTERVIEW_ROUNDS = [
  "First",
  "Second",
  "Third",
  "Fourth",
  "Fifth",
  "Sixth",
  "Seventh",
  "Eighth"
];

export const REVIEW_ROUND_INITIAL = "Initial Review";
export const REVIEW_ROUND_APPLICATION = "Application";
export const REVIEW_ROUND_MID = "Mid-Review";
export const REVIEW_ROUND_MID_SECOND = "Mid-Review (Second)";
export const REVIEW_ROUND_MID_THIRD = "Mid-Review (Third)";
export const REVIEW_ROUND_FINAL = "Final Review";

export const REVIEW_ROUNDS = [
  REVIEW_ROUND_INITIAL,
  REVIEW_ROUND_APPLICATION,
  REVIEW_ROUND_MID,
  REVIEW_ROUND_MID_SECOND,
  REVIEW_ROUND_MID_THIRD,
  REVIEW_ROUND_FINAL
];

// hiring
export const VIRTUAL_ROUND_OFFERED = "Offered";
export const VIRTUAL_ROUND_OFFER_DECLINED = "Offer Declined";
export const VIRTUAL_ROUND_HIRED = "Hired";
export const VIRTUAL_ROUND_ARCHIVED = "Archived";
export const HIRING_VIRTUAL_ROUNDS = new Set([
  VIRTUAL_ROUND_OFFERED,
  VIRTUAL_ROUND_OFFER_DECLINED,
  VIRTUAL_ROUND_HIRED,
  VIRTUAL_ROUND_ARCHIVED
]);

// sales
export const VIRTUAL_ROUND_SALES_DEAL_PENDING = "Deal Pending";
export const VIRTUAL_ROUND_SALES_DEAL_DECLINED = "Deal Declined";
export const VIRTUAL_ROUND_SALES_DEAL_ACCEPTED = "Contract Signed";
export const VIRTUAL_ROUND_SALES_PAID = "Paid";
export const VIRTUAL_ROUND_SALES_ARCHIVED = "Archived";

export const INTERVIEW_ROUNDS_AS_OPTIONS = INTERVIEW_ROUNDS.map((round) => ({
  value: round,
  label: round
}));

export const REVIEW_ROUNDS_AS_OPTIONS = REVIEW_ROUNDS.map((round) => ({
  value: round,
  label: round
}));

export const getSalesRounds = () => [
  {
    round: VIRTUAL_ROUND_SALES_DEAL_PENDING,
    name: VIRTUAL_ROUND_SALES_DEAL_PENDING,
    type: SALES_DEAL_PENDING_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_SALES_DEAL_DECLINED,
    name: VIRTUAL_ROUND_SALES_DEAL_DECLINED,
    type: SALES_DEAL_DECLINED_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_SALES_DEAL_ACCEPTED,
    name: VIRTUAL_ROUND_SALES_DEAL_ACCEPTED,
    type: SALES_DEAL_ACCEPTED_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_SALES_PAID,
    name: VIRTUAL_ROUND_SALES_PAID,
    type: SALES_DEAL_PAID_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_SALES_ARCHIVED,
    name: VIRTUAL_ROUND_SALES_ARCHIVED,
    type: SALES_DEAL_ARCHIVED_WORKFLOW_TYPE,
    final: true
  }
];

// hiring
export const REVIEW_WORKFLOW_TYPE = "review";
export const APPLICATION_QUESTION_WORKFLOW_TYPE = "application";
export const RESUME_REVIEW_WORKFLOW_TYPE = "resume_review";
export const FINALIST_REVIEW_WORKFLOW_TYPE = "finalist_review";
export const SCREENING_WORKFLOW_TYPE = "screening";
export const INTERVIEW_WORKFLOW_TYPE = "interview";
export const OFFERED_WORKFLOW_TYPE = "offered";
export const OFFER_DECLINED_WORKFLOW_TYPE = "offer_declined";
export const HIRED_WORKFLOW_TYPE = "hired";
export const ARCHIVED_WORKFLOW_TYPE = "archived";
export const SOURCED_NEW_WORKFLOW_TYPE = "sourced_new";

/*

offered
offer_declined
hired
archived

*/

export const WORFLOW_COLORS_BY_TYPE = {
  [OFFERED_WORKFLOW_TYPE]: {
    borderColor: "border-blue-700",
    bgColor: "bg-green-100",
    textColor: "text-white"
  },
  [OFFER_DECLINED_WORKFLOW_TYPE]: {
    borderColor: "border-red-500",
    bgColor: "bg-red-100",
    textColor: "text-white"
  },
  [HIRED_WORKFLOW_TYPE]: {
    borderColor: "border-green-700",
    bgColor: "bg-green-100",
    textColor: "text-white"
  },
  [ARCHIVED_WORKFLOW_TYPE]: {
    borderColor: "border-gray-500",
    bgColor: "bg-gray-100",
    textColor: "text-white"
  },
  [APPLICATION_QUESTION_WORKFLOW_TYPE]: {
    borderColor: "border-blue-500",
    bgColor: "bg-blue-100",
    textColor: "text-white"
  },
  [RESUME_REVIEW_WORKFLOW_TYPE]: {
    borderColor: "border-gray-500",
    bgColor: "bg-gray-100",
    textColor: "text-white"
  },
  [SCREENING_WORKFLOW_TYPE]: {
    borderColor: "border-yellow-500",
    bgColor: "bg-yellow-100",
    textColor: "text-gray-800"
  },
  [INTERVIEW_WORKFLOW_TYPE]: {
    borderColor: "border-green-500",
    bgColor: "bg-green-100",
    textColor: "text-white"
  },
  [FINALIST_REVIEW_WORKFLOW_TYPE]: {
    borderColor: "border-purple-500",
    bgColor: "bg-purple-100",
    textColor: "text-white"
  },
  [REVIEW_WORKFLOW_TYPE]: {
    borderColor: "border-purple-500",
    bgColor: "bg-purple-100",
    textColor: "text-white"
  },
  ["unallocated"]: {
    borderColor: "border-black",
    bgColor: "bg-gray-100",
    textColor: "text-white"
  }
};

export const SOURCING_WORKFLOW_ROUND_TYPES = new Set([
  SOURCED_NEW_WORKFLOW_TYPE
]);

export const VIRTUAL_WORKFLOW_ROUND_TYPES = new Set([
  OFFERED_WORKFLOW_TYPE,
  OFFER_DECLINED_WORKFLOW_TYPE,
  HIRED_WORKFLOW_TYPE,
  ARCHIVED_WORKFLOW_TYPE
]);

export const SCHEDULING_WORKFLOW_ROUND_TYPES = new Set([
  SCREENING_WORKFLOW_TYPE,
  INTERVIEW_WORKFLOW_TYPE
]);

export const WORKFLOW_TYPES = [
  INTERVIEW_WORKFLOW_TYPE,
  SCREENING_WORKFLOW_TYPE,
  REVIEW_WORKFLOW_TYPE
];

export const SOURCED_ROUNDS = [
  {
    round: SOURCED_NEW_WORKFLOW_TYPE,
    name: "Lead",
    type: SOURCED_NEW_WORKFLOW_TYPE,
    final: true
  }
];

export const WORKFLOW_DURATION_OPTIONS = [
  { value: 0, label: "n/a" },
  { value: 15, label: "15 mins" },
  { value: 30, label: "30 mins" },
  { value: 45, label: "45 mins" },
  { value: 60, label: "1 hour" },
  { value: 75, label: "1 hour, 15 mins" },
  { value: 90, label: "1 hour, 30 mins" },
  { value: 105, label: "1 hour, 45 mins" },
  { value: 120, label: "2 hours" },
  { value: 135, label: "2 hour, 15 mins" },
  { value: 150, label: "2 hour, 30 mins" },
  { value: 165, label: "2 hour, 45 mins" },
  { value: 180, label: "3 hours" }
];

export const WORKFLOW_DURATION_OPTIONS_BY_MINS =
  WORKFLOW_DURATION_OPTIONS.reduce(
    (acc, { value, label }) => ({ ...acc, [value]: label }),
    {}
  );

export const WORKFLOW_DESCRIPTIONS = {
  [REVIEW_WORKFLOW_TYPE]:
    "A round for discussing candidates at a critical point in the hiring process.",
  [SCREENING_WORKFLOW_TYPE]:
    "An lower-effort interview stage for pairing down the candidate pool to the most qualified candidates.",
  [INTERVIEW_WORKFLOW_TYPE]:
    "In depth session to evaluate candidate and gather compelling evidence for hiring decision."
};

export const WORKFLOW_TYPES_AS_OPTIONS = WORKFLOW_TYPES.map((type) => ({
  value: type,
  label: capitalize(type),
  description: WORKFLOW_DESCRIPTIONS[type]
}));

export const OFFER_NONE = "none";
export const OFFER_PENDING = "pending";
export const OFFER_DECLINED = "declined";
export const OFFER_ACCEPTED = "accepted";

export const OFFER_STATUSES = [OFFER_PENDING, OFFER_DECLINED, OFFER_ACCEPTED];
export const OFFER_STATUSES_AS_OPTIONS = OFFER_STATUSES.map((offer) => ({
  label: offer.charAt(0).toUpperCase() + offer.slice(1),
  value: offer
}));

export const getHiringRounds = () => [
  {
    round: VIRTUAL_ROUND_OFFERED,
    name: VIRTUAL_ROUND_OFFERED,
    type: OFFERED_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_OFFER_DECLINED,
    name: VIRTUAL_ROUND_OFFER_DECLINED,
    type: OFFER_DECLINED_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_HIRED,
    name: VIRTUAL_ROUND_HIRED,
    type: HIRED_WORKFLOW_TYPE,
    final: true
  },
  {
    round: VIRTUAL_ROUND_ARCHIVED,
    name: VIRTUAL_ROUND_ARCHIVED,
    type: ARCHIVED_WORKFLOW_TYPE,
    final: true
  }
];

// sales
export const SALES_BUCKET_WORKFLOW_TYPE = "bucket";
export const SALES_LEAD_WORKFLOW_TYPE = "lead";
export const SALES_PROSPECT_WORKFLOW_TYPE = "prospect";
export const SALES_DEAL_PENDING_WORKFLOW_TYPE = "deal_pending";
export const SALES_DEAL_DECLINED_WORKFLOW_TYPE = "deal_declined";
export const SALES_DEAL_ACCEPTED_WORKFLOW_TYPE = "deal_accepted";
export const SALES_DEAL_PAID_WORKFLOW_TYPE = "deal_paid";
export const SALES_DEAL_ARCHIVED_WORKFLOW_TYPE = "archived";

export const SALES_VIRTUAL_WORKFLOW_ROUND_TYPES = new Set([
  SALES_DEAL_PENDING_WORKFLOW_TYPE,
  SALES_DEAL_DECLINED_WORKFLOW_TYPE,
  SALES_DEAL_ACCEPTED_WORKFLOW_TYPE,
  SALES_DEAL_PAID_WORKFLOW_TYPE,
  SALES_DEAL_ARCHIVED_WORKFLOW_TYPE
]);

export const SALES_SCHEDULING_WORKFLOW_ROUND_TYPES = new Set([
  SALES_PROSPECT_WORKFLOW_TYPE
]);

export const SALES_DEAL_PENDING = "pending";
export const SALES_DEAL_DECLINED = "declined";
export const SALES_DEAL_ACCEPTED = "accepted";

export const SALES_DEAL_STATUSES = [
  SALES_DEAL_PENDING,
  SALES_DEAL_DECLINED,
  SALES_DEAL_ACCEPTED
];
export const SALES_DEAL_STATUSES_AS_OPTIONS = SALES_DEAL_STATUSES.map(
  (offer) => ({
    label: offer.charAt(0).toUpperCase() + offer.slice(1),
    value: offer
  })
);

export const RECOMMENDATION_YES = "Yes";
export const RECOMMENDATION_NO = "No";
export const RECOMMENDATION_STRONG_YES = "Strong Yes";
export const RECOMMENDATION_STRONG_NO = "Strong No";

export const DEMOGRAPHIC_STATUS_ESTIMATED = "Estimated";
export const DEMOGRAPHIC_STATUS_DECLINED = "Declined";
export const DEMOGRAPHIC_STATUS_PENDING = "Pending";
export const DEMOGRAPHIC_STATUS_PROVIDED = "Provided";

export const CANDIDATE_ACTION_REGRESS = "shift up";
export const CANDIDATE_ACTION_ADVANCE = "advance";
export const CANDIDATE_ACTION_ARCHIVE = "archive";
export const CANDIDATE_ACTION_OFFER = "offer";
export const CANDIDATE_ACTION_SCHEDULE = "schedule";
export const CANDIDATE_ACTION_OFFER_UPDATE = "offer_update";
export const CANDIDATE_ACTION_OFFER_ACCEPT = "offer_accept";
export const CANDIDATE_ACTION_OFFER_DECLINE = "offer_decline";
export const CANDIDATE_ACTION_REVIVE = "revive";

export const CANDIDATE_ACTION_TO_ENTITY = {
  [CANDIDATE_ACTION_ADVANCE]: candidateAdvanceEntity,
  [CANDIDATE_ACTION_ARCHIVE]: jobCandidateArchiveEntity,
  [CANDIDATE_ACTION_OFFER]: jobCandidateOfferEntity,
  [CANDIDATE_ACTION_OFFER_ACCEPT]: jobCandidateOfferAcceptEntity,
  [CANDIDATE_ACTION_OFFER_DECLINE]: jobCandidateOfferDeclineEntity
};

export const DEMOGRAPHICS_TYPE_GENDER = "Gender";
export const DEMOGRAPHICS_TYPE_RACE = "Race";
export const DEMOGRAPHICS_TYPE_ETHNICITY = "Ethnicity";
export const DEMOGRAPHICS_TYPE_RACE_ETHNICITY = "Race & Ethnicity Combined";
export const DEMOGRAPHICS_TYPE_LATINX = "LatinX Yes/No";
export const DEMOGRAPHICS_TYPE_VETERAN = "Veteran Status";
export const DEMOGRAPHICS_TYPE_DISABILITY = "Disability";
export const DEMOGRAPHICS_TYPE_DISABILITY_DETAILS = "Disability Detail";
export const DEMOGRAPHICS_TYPE_SEXUAL_ORIENTATION = "Sexual Orientation";
export const DEMOGRAPHICS_TYPE_BIRTH_YEAR = "Birth Year";
export const DEMOGRAPHICS_TYPE_PRIMARY_CARE_GIVER =
  "Primary Care Giver (unpaid)";
export const DEMOGRAPHICS_TYPE_MILITARY_DOMESTIC_PARTNER =
  "Military Domestic Partner";
export const DEMOGRAPHICS_TYPE_OTHER = "Other";

export const DEMOGRAPHIC_TYPES = [
  DEMOGRAPHICS_TYPE_GENDER,
  DEMOGRAPHICS_TYPE_RACE,
  DEMOGRAPHICS_TYPE_ETHNICITY,
  DEMOGRAPHICS_TYPE_RACE_ETHNICITY,
  DEMOGRAPHICS_TYPE_LATINX,
  DEMOGRAPHICS_TYPE_VETERAN,
  DEMOGRAPHICS_TYPE_DISABILITY,
  DEMOGRAPHICS_TYPE_DISABILITY_DETAILS,
  DEMOGRAPHICS_TYPE_SEXUAL_ORIENTATION,
  DEMOGRAPHICS_TYPE_BIRTH_YEAR,
  DEMOGRAPHICS_TYPE_PRIMARY_CARE_GIVER,
  DEMOGRAPHICS_TYPE_MILITARY_DOMESTIC_PARTNER,
  DEMOGRAPHICS_TYPE_OTHER
];

export const DEMOGRAPHIC_TYPE_OPTIONS = DEMOGRAPHIC_TYPES.map((option) => ({
  label: option,
  value: option
}));

export const BENEFIT_HEALTH_INSURANCE = "Health Insurance";
export const BENEFIT_DENTAL_INSURANCE = "Dental Insurance";
export const BENEFIT_VISION_INSURANCE = "Vision Insurance";
export const BENEFIT_PET_INSURANCE = "Pet Insurance";
export const BENEFIT_MENTAL_HEALTH = "Mental Health";
export const BENEFIT_MEDICAL_CARE_MANAGEMENT = "Medical Care Management";
export const BENEFIT_ADDICTION = "Addiction";
export const BENEFIT_TELEHEALTH = "Telehealth";
export const BENEFIT_MEDITATION = "Meditation & Mindfulness";
export const BENEFIT_GYM = "Gym";
export const BENEFIT_FITNESS = "Fitness";
export const BENEFIT_YOGA = "Yoga";
export const BENEFIT_WELLNESS = "Wellness";
export const BENEFIT_FERTILITY = "Fertility";
export const BENEFIT_FAMILY_CARE_SERVICES = "Family Care Services";
export const BENEFIT_ADOPTION_ASSISTANCE = "Adoption Assistance";
export const BENEFIT_HSA = "Health Savings Account";
export const BENEFIT_FSA = "Flexible Spending Account";
export const BENEFIT_401K = "401k";
export const BENEFIT_FINANCIAL_WELLNESS = "Financial Wellness";
export const BENEFIT_STUDENT_LOAN_REPAYMENT = "Student Loan Repayment";
export const BENEFIT_TUITION_ASSISTANCE = "Tuition Assistance";
export const BENEFIT_LIFE_INSURANCE = "Life Insurance";
export const BENEFIT_SHORT_TERM_DISABILITY = "Short Term Disability";
export const BENEFIT_LONG_TERM_DISABILITY = "Long Term Disability";
export const BENEFIT_COMMUTER = "Commuter";
export const BENEFIT_PET_CARE = "Pet Care";
export const BENEFIT_MEAL_ALLOWANCE = "Meal Allowance";
export const BENEFIT_HYBRID_WORK = "Hybrid Work Environment";
export const BENEFIT_FULLY_REMOTE = "Fully Remote";
export const BENEFIT_SOCIAL_MISSION = "Mission Focused";
export const BENEFIT_PTO = "Paid Time Off";
export const BENEFIT_VACATION = "Vacation Time";
export const BENEFIT_TRAINING = "Training & Mentorship";
export const BENEFIT_CONFERENCE = "Conference Budget";
export const BENEFIT_FLEXIBLE_HOURS = "Flexible Hours";
export const BENEFIT_PAID_FAMILY_LEAVE = "Paid Family Leave";
export const BENEFIT_FOUR_DAY_WORK = "Four Day Work Week";
export const BENEFIT_FOOD = "Free Food";
export const BENEFIT_SNACKS = "Free Snacks";
export const BENEFIT_COFFEE = "Free Coffee";
export const BENEFIT_PET_FRIENDLY_OFFICE = "Pet Friendly Office";
export const BENEFIT_RETREATS = "Retreats";
export const BENEFIT_EMPLOYEE_OUTINGS = "Employee Outings";
export const BENEFIT_DAY_CARE = "Day Care Expenses";

export const BENEFIT_TYPES = [
  BENEFIT_HEALTH_INSURANCE,
  BENEFIT_DENTAL_INSURANCE,
  BENEFIT_VISION_INSURANCE,
  BENEFIT_PET_INSURANCE,
  BENEFIT_MENTAL_HEALTH,
  BENEFIT_MEDICAL_CARE_MANAGEMENT,
  BENEFIT_ADDICTION,
  BENEFIT_TELEHEALTH,
  BENEFIT_MEDITATION,
  BENEFIT_GYM,
  BENEFIT_FITNESS,
  BENEFIT_YOGA,
  BENEFIT_WELLNESS,
  BENEFIT_FERTILITY,
  BENEFIT_FAMILY_CARE_SERVICES,
  BENEFIT_ADOPTION_ASSISTANCE,
  BENEFIT_HSA,
  BENEFIT_FSA,
  BENEFIT_401K,
  BENEFIT_FINANCIAL_WELLNESS,
  BENEFIT_STUDENT_LOAN_REPAYMENT,
  BENEFIT_TUITION_ASSISTANCE,
  BENEFIT_LIFE_INSURANCE,
  BENEFIT_SHORT_TERM_DISABILITY,
  BENEFIT_LONG_TERM_DISABILITY,
  BENEFIT_COMMUTER,
  BENEFIT_PET_CARE,
  BENEFIT_MEAL_ALLOWANCE,
  BENEFIT_HYBRID_WORK,
  BENEFIT_FULLY_REMOTE,
  BENEFIT_SOCIAL_MISSION,
  BENEFIT_PTO,
  BENEFIT_VACATION,
  BENEFIT_TRAINING,
  BENEFIT_CONFERENCE,
  BENEFIT_FLEXIBLE_HOURS,
  BENEFIT_PAID_FAMILY_LEAVE,
  BENEFIT_FOUR_DAY_WORK,
  BENEFIT_FOOD,
  BENEFIT_SNACKS,
  BENEFIT_COFFEE,
  BENEFIT_PET_FRIENDLY_OFFICE,
  BENEFIT_RETREATS,
  BENEFIT_EMPLOYEE_OUTINGS,
  BENEFIT_DAY_CARE
];

export const APPOINTMENT_STATUS_UNSCHEDULED = "unscheduled";
export const APPOINTMENT_STATUS_PENDING = "pending";
export const APPOINTMENT_STATUS_COMPLETE = "complete";

export const JOB_REQUIREMENT_MUST = "must";
export const JOB_REQUIREMENT_NICE = "nice";
export const JOB_REQUIREMENT_NOT = "not";

export const JOB_REQUIREMENTS = {
  [JOB_REQUIREMENT_MUST]: "Must-have",
  [JOB_REQUIREMENT_NICE]: "Nice-to-have",
  [JOB_REQUIREMENT_NOT]: "Not-have"
};

export const JOB_REQUIREMENT_OPTIONS = Object.keys(JOB_REQUIREMENTS).map(
  (key) => ({ label: JOB_REQUIREMENTS[key], value: key })
);

export const REJECTION_NOTICE_INITIAL = "REJECTION_NOTICE_INITIAL";
export const REJECTION_NOTICE_MID = "REJECTION_NOTICE_MID";
export const REJECTION_NOTICE_FINAL = "REJECTION_NOTICE_FINAL";

export const ORIGIN_TYPE_APPLIED = "applied";
export const ORIGIN_TYPE_SOURCED = "sourced";
export const ORIGIN_TYPE_REFERRED = "referred";
export const ORIGIN_TYPE_INTERNAL = "internal";
export const ORIGIN_TYPE_NOMINATED = "nominated";
export const ORIGIN_TYPE_EXTERNAL = "recruited";
export const ORIGIN_TYPE_FAIR = "fair";

export const ORIGIN_TYPE_COLORS = {
  [ORIGIN_TYPE_APPLIED]: ["bg-blue-100", "text-indigo-800"],
  [ORIGIN_TYPE_FAIR]: ["bg-blue-100", "text-indigo-800"],
  [ORIGIN_TYPE_SOURCED]: ["bg-yellow-100", "text-indigo-800"],
  [ORIGIN_TYPE_REFERRED]: ["bg-emerald-100", "text-indigo-800"],
  [ORIGIN_TYPE_INTERNAL]: ["bg-emerald-100", "text-indigo-800"],
  [ORIGIN_TYPE_NOMINATED]: ["bg-emerald-100", "text-indigo-800"],
  [ORIGIN_TYPE_EXTERNAL]: ["bg-gray-300", "text-indigo-800"]
};

export const ORIGIN_TYPE_OPTIONS = [
  { value: ORIGIN_TYPE_APPLIED, label: "Applied" },
  { value: ORIGIN_TYPE_SOURCED, label: "Sourced" },
  { value: ORIGIN_TYPE_REFERRED, label: "Referred" },
  { value: ORIGIN_TYPE_INTERNAL, label: "Internal" },
  { value: ORIGIN_TYPE_NOMINATED, label: "Nominated" },
  { value: ORIGIN_TYPE_EXTERNAL, label: "External" },
  { value: ORIGIN_TYPE_FAIR, label: "Job Fair" }
];

export const ORIGIN_TYPE_PAST_TENSE = {
  [ORIGIN_TYPE_APPLIED]: "applied on",
  [ORIGIN_TYPE_SOURCED]: "sourced on",
  [ORIGIN_TYPE_REFERRED]: "referred on",
  [ORIGIN_TYPE_INTERNAL]: "applied (internal) on",
  [ORIGIN_TYPE_NOMINATED]: "nominated on",
  [ORIGIN_TYPE_EXTERNAL]: "recruited on",
  [ORIGIN_TYPE_FAIR]: "applied (job fair) on"
};

export const ORIGIN_TYPES_BY_ID = ORIGIN_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const SOURCE_LABEL_BY_ORIGIN_TYPE = {
  [ORIGIN_TYPE_APPLIED]: "Application",
  [ORIGIN_TYPE_SOURCED]: "Source",
  [ORIGIN_TYPE_REFERRED]: "Referrer",
  [ORIGIN_TYPE_NOMINATED]: "Nominated by",
  [ORIGIN_TYPE_EXTERNAL]: "External Recruiter",
  [ORIGIN_TYPE_FAIR]: "Job Fair Event"
};

export const ORG_ACCOUNT_TYPE_HIRING = "hiring";
export const ORG_ACCOUNT_TYPE_SALES = "sales";
export const ORG_ACCOUNT_TYPE_CLINICAL = "clinical";

export const ORG_ACCOUNT_TYPE_OPTIONS = [
  { label: "Hiring", value: ORG_ACCOUNT_TYPE_HIRING },
  { label: "Sales", value: ORG_ACCOUNT_TYPE_SALES }
];

export const ORG_ACCOUNT_TYPES_BY_ID = ORG_ACCOUNT_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const JOB_TASK_FREQ_RARE = "Rare";
export const JOB_TASK_FREQ_SOMETIMES = "Sometimes";
export const JOB_TASK_FREQ_OFTEN = "Often";

export const JOB_TASK_FREQ_BY_ID = {
  1: JOB_TASK_FREQ_RARE,
  2: JOB_TASK_FREQ_SOMETIMES,
  3: JOB_TASK_FREQ_OFTEN
};

export const JOB_TASK_FREQ_OPTIONS = Object.keys(JOB_TASK_FREQ_BY_ID).map(
  (key) => ({ label: JOB_TASK_FREQ_BY_ID[key], value: parseInt(key, 10) })
);

export const JOB_TASK_IMPORTANCE_OPTION_NEUTRAL = "Neutral";
export const JOB_TASK_IMPORTANCE_OPTION_IMPORTANT = "Important";
export const JOB_TASK_IMPORTANCE_OPTION_CRITICAL = "Critical";

export const JOB_TASK_IMPORTANCE_BY_ID = {
  1: JOB_TASK_IMPORTANCE_OPTION_NEUTRAL,
  2: JOB_TASK_IMPORTANCE_OPTION_IMPORTANT,
  3: JOB_TASK_IMPORTANCE_OPTION_CRITICAL
};

export const JOB_TASK_IMPORTANCE_OPTIONS = Object.keys(
  JOB_TASK_IMPORTANCE_BY_ID
).map((key) => ({
  label: JOB_TASK_IMPORTANCE_BY_ID[key],
  value: parseInt(key, 10)
}));

export const JOB_MESSAGE_SEND_DELAY_INSTANT = "Send immediately";
export const JOB_MESSAGE_SEND_DELAY_ROUND_ENTER = "Send on round change";

export const JOB_MESSAGE_SEND_DELAY_OPTIONS = [
  { value: -1, label: JOB_MESSAGE_SEND_DELAY_INSTANT },
  { value: 0, label: JOB_MESSAGE_SEND_DELAY_ROUND_ENTER }
].concat(
  [...Array(90).keys()].map((key) => ({
    label: `${key + 1} day${key > 0 ? "s" : ""} in round`,
    value: key + 1
  }))
);

export const CONTENT_PRESENCE_JOB_ANALYSIS = "job-analysis";

export const EMPLOYMENT_TYPE_FULL = "full-time";
export const EMPLOYMENT_TYPE_PART = "part-time";

export const EMPLOYMENT_TYPE_OPTIONS = [
  { label: "Full-time", value: EMPLOYMENT_TYPE_FULL },
  { label: "Part-time", value: EMPLOYMENT_TYPE_PART }
];

export const EMPLOYMENT_TYPES_BY_ID = EMPLOYMENT_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const REMOTE_TYPE_ONSITE = "onsite";
export const REMOTE_TYPE_REMOTE = "remote";
export const REMOTE_TYPE_HYBRID = "hybrid";

export const REMOTE_TYPE_OPTIONS = [
  { label: "On-Site", value: REMOTE_TYPE_ONSITE },
  { label: "Remote", value: REMOTE_TYPE_REMOTE },
  { label: "Hybrid", value: REMOTE_TYPE_HYBRID }
];

export const REMOTE_TYPES_BY_ID = REMOTE_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const CANDIDATE_EVENT_ADDED = "pipeline_added";
export const CANDIDATE_EVENT_ARCHIVED = "archived";
export const CANDIDATE_EVENT_OFFERED = "offered";
export const CANDIDATE_EVENT_DECLINED = "declined";
export const CANDIDATE_EVENT_ACCEPTED = "accepted";
export const CANDIDATE_EVENT_ADVANCED = "advanced_round";
export const CANDIDATE_EVENT_NOTE = "note";
export const CANDIDATE_EVENT_EMAIL = "email";
export const CANDIDATE_EVENT_TASK = "task";
export const CANDIDATE_EVENT_CALL = "call";
export const CANDIDATE_EVENT_SOCIAL_CONNECT = "social_connect";
export const CANDIDATE_EVENT_SOCIAL_ENGAGE = "social_engage";
export const CANDIDATE_EVENT_DIRECT_MAIL = "direct_mail";

export const CADENCE_STEP_TYPES = [
  {
    value: CANDIDATE_EVENT_EMAIL,
    label: "Email",
    description: "Send a prepared email.",
    Icon: PaperAirplaneIcon
  },
  {
    value: CANDIDATE_EVENT_CALL,
    label: "Call",
    description: "Call directly over the phone.",
    Icon: DevicePhoneMobileIcon
  },
  {
    value: CANDIDATE_EVENT_TASK,
    label: "Task",
    description: "Create a custom task.",
    Icon: CheckCircleIcon
  },
  {
    value: CANDIDATE_EVENT_SOCIAL_CONNECT,
    label: "Social Connect",
    description: "Connect on social media.",
    Icon: FaceSmileIcon
  },
  {
    value: CANDIDATE_EVENT_SOCIAL_ENGAGE,
    label: "Social Engage",
    description: "Engage on social media.",
    Icon: HandThumbUpIcon
  },
  {
    value: CANDIDATE_EVENT_DIRECT_MAIL,
    label: "Direct Mail",
    description: "Send a physical letter or package.",
    Icon: GiftIcon
  }
];

export const ONBOARDING_TASK_DONE = "done";
export const ONBOARDING_TASK_TODO = "todo";

export const MEETING_TYPE_VIDEO = "video";
export const MEETING_TYPE_PHONE = "phone";
export const MEETING_TYPE_IN_PERSON = "in-person";

export const MEETING_TYPE_OPTIONS = [
  { label: "Video Meeting", value: MEETING_TYPE_VIDEO },
  { label: "Phone Call", value: MEETING_TYPE_PHONE },
  { label: "In-Person Meeting", value: MEETING_TYPE_IN_PERSON }
];

export const MEETING_TYPE_ICONS_BY_ID = {
  [MEETING_TYPE_VIDEO]: VideoCameraIcon,
  [MEETING_TYPE_PHONE]: PhoneIcon,
  [MEETING_TYPE_IN_PERSON]: MapPinIcon
};

export const MEETING_TYPE_OPTIONS_BY_ID = MEETING_TYPE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const PHONE_PREFERENCE_INTERVIEWER = "interviewer";
export const PHONE_PREFERENCE_CANDIDATE = "candidate";
export const PHONE_PREFERENCE_CONFERENCE = "conference";

export const PHONE_PREFERENCE_OPTIONS = [
  {
    label: "Call the candidate",
    value: PHONE_PREFERENCE_CANDIDATE
  },
  { label: "Candidate calls us", value: PHONE_PREFERENCE_INTERVIEWER },
  {
    label: "Dial-in Number provided to all",
    value: PHONE_PREFERENCE_CONFERENCE
  }
];

export const PHONE_PREFERENCE_OPTIONS_BY_ID = PHONE_PREFERENCE_OPTIONS.reduce(
  (acc, { label, value }) => ({ ...acc, [value]: label }),
  {}
);

export const MESSAGE_TEMPLATE_SELF_SCHEDULING_GENERIC =
  "SELF_SCHEDULING_GENERIC";
export const MESSAGE_TEMPLATE_REJECTION_NOTICE_INITIAL =
  REJECTION_NOTICE_INITIAL;
export const MESSAGE_TEMPLATE_REJECTION_NOTICE_MID = REJECTION_NOTICE_MID;
export const MESSAGE_TEMPLATE_REJECTION_NOTICE_FINAL = REJECTION_NOTICE_FINAL;
export const MESSAGE_TEMPLATE_APPLICATION_CONFIRMATION =
  "APPLICATION_CONFIRMATION";

export const JOB_REQUIREMENT_COMPETENCY = "competency";
export const JOB_REQUIREMENT_ATTRIBUTE = "attribute";
export const JOB_REQUIREMENT_JUDGEMENT = "judgment";
export const JOB_REQUIREMENT_TOOL = "tool";
export const JOB_REQUIREMENT_EXPERIENCE = "experience";
export const JOB_REQUIREMENT_METRIC = "metric";

export const JOB_REQUIREMENT_LETTERS = {
  [JOB_REQUIREMENT_COMPETENCY]: "S",
  [JOB_REQUIREMENT_ATTRIBUTE]: "A",
  [JOB_REQUIREMENT_JUDGEMENT]: "J",
  [JOB_REQUIREMENT_TOOL]: "T",
  [JOB_REQUIREMENT_EXPERIENCE]: "E",
  [JOB_REQUIREMENT_METRIC]: "M"
};

export const JOB_REQUIREMENT_CONTENT_TYPES = {
  [JOB_REQUIREMENT_COMPETENCY]: "Skill",
  [JOB_REQUIREMENT_ATTRIBUTE]: "Personal Attribute",
  [JOB_REQUIREMENT_JUDGEMENT]: "Judgment",
  [JOB_REQUIREMENT_TOOL]: "Tool",
  [JOB_REQUIREMENT_EXPERIENCE]: "Experience",
  [JOB_REQUIREMENT_METRIC]: "Metric"
};

export const JOB_REQUIREMENT_CONTENT_TYPES_DESCRIPTION = {
  [JOB_REQUIREMENT_COMPETENCY]:
    "A developed ability enabling a person to carry out specific job-related tasks.",
  [JOB_REQUIREMENT_ATTRIBUTE]:
    "An inherent characteristic, trait, or attribute that shapes an individual's work.",
  [JOB_REQUIREMENT_JUDGEMENT]:
    "An area where a person needs to make sound decisions or form sensible conclusions.",
  [JOB_REQUIREMENT_TOOL]:
    "Expertise in a specific software program or instrument required for the job.",
  [JOB_REQUIREMENT_EXPERIENCE]:
    "Previous real-world involvement in relevant job duties, projects, or situations.",
  [JOB_REQUIREMENT_METRIC]:
    "A specific degree of performance needed for the job."
};

export const EVALUATION_TYPE_QUESTION = "question";
export const EVALUATION_TYPE_TEST = "test";
export const EVALUATION_TYPE_SIMULATION = "simulation";
export const EVALUATION_TYPE_PRESENTATION = "presentation";
export const EVALUATION_TYPE_JUDGMENT = "judgment";

export const EVALUATION_TYPES = {
  [EVALUATION_TYPE_QUESTION]: "Question",
  [EVALUATION_TYPE_TEST]: "Skill Test",
  [EVALUATION_TYPE_SIMULATION]: "Work Simulation",
  [EVALUATION_TYPE_PRESENTATION]: "Presentation",
  [EVALUATION_TYPE_JUDGMENT]: "Judgment"
};

export const EVALUATION_TYPE_DESCRIPTIONS = {
  [EVALUATION_TYPE_QUESTION]:
    "Prompts the candidate to tell a story based on their past actions.",
  [EVALUATION_TYPE_TEST]:
    "An assessment of the candidate’s skill or ability proficiency.",
  [EVALUATION_TYPE_SIMULATION]:
    "A realistic scenario replicating a typical work task or challenge.",
  [EVALUATION_TYPE_PRESENTATION]:
    "Allows user to demonstrate communication and presentation abilities.",
  [EVALUATION_TYPE_JUDGMENT]:
    "Presents hypothetical scenario to assess decision-making skills."
};

export const RATING_LABELS = [
  "No Credit",
  "Poor",
  "Satisfactory",
  "Good",
  "Excellent"
];

export const JOB_ROLE_FULL = "full";
export const JOB_ROLE_STANDARD = "standard";

export const JOB_ROLES_BY_NAME = {
  [JOB_ROLE_FULL]: {
    name: "Full Access",
    description:
      "User can manage all aspects of this job and candidate pipeline."
  },
  [JOB_ROLE_STANDARD]: {
    name: "Standard Access",
    description:
      "User has access to selected features of this job and/or pipeline."
  }
};

export const JOB_PERMISSION_FORM = "form";
export const JOB_PERMISSION_EVALUATION = "evaluation";
export const JOB_PERMISSION_DESCRIPTION = "description";
export const JOB_PERMISSION_PANEL = "panel";

export const PIPELINE_PERMISSION_LIST = "list";
export const PIPELINE_PERMISSION_SCORECARDS = "scorecards";
export const PIPELINE_PERMISSION_RESUME = "resume";

export const JOB_PERMISSIONS_BY_NAME = {
  [JOB_PERMISSION_FORM]: {
    name: "Job Details",
    description: "User can edit the job form."
  },
  [JOB_PERMISSION_EVALUATION]: {
    name: "Evaluation Content",
    description: "User can edit the job evaluation content."
  },
  [JOB_PERMISSION_DESCRIPTION]: {
    name: "Job Description",
    description: "User can edit the job description."
  },
  [JOB_PERMISSION_PANEL]: {
    name: "Panel & Rounds",
    description: "User can edit the job panel and rounds."
  },
  [PIPELINE_PERMISSION_LIST]: {
    name: "Manage Pipeline",
    description: "User can manage the candidate pipeline."
  },
  [PIPELINE_PERMISSION_SCORECARDS]: {
    name: "Scorecards",
    description: "User can view candidate scorecards."
  },
  [PIPELINE_PERMISSION_RESUME]: {
    name: "Resume Screening",
    description: "User can screen candidate resumes."
  }
};

export const EMAIL_DIRECTION_INBOUND = "inbound";
export const EMAIL_DIRECTION_OUTBOUND = "outbound";

// AI Suggestion Cache
export const AI_JOB_CLASSIFICATION = "AI_JOB_CLASSIFICATION";
export const AI_JOB_EXPERTS = "AI_JOB_EXPERTS";
export const AI_JOB_DISCIPLINES = "AI_JOB_DISCIPLINES";
export const AI_PROCESS_TASKS = "AI_PROCESS_TASKS";
export const AI_PROCESS_REQUIREMENTS = "AI_PROCESS_REQUIREMENTS";
export const AI_PROCESS_SIGNALS = "AI_PROCESS_SIGNALS";
export const AI_PROCESS_QUESTIONS = "AI_PROCESS_QUESTIONS";

export const AI_PROCESSES_BY_INDEX = [
  AI_JOB_CLASSIFICATION,
  AI_JOB_EXPERTS,
  AI_JOB_DISCIPLINES,
  AI_PROCESS_TASKS,
  AI_PROCESS_REQUIREMENTS,
  AI_PROCESS_SIGNALS,
  AI_PROCESS_QUESTIONS
];

export const CAMPAIGN_TABS = {
  prospects: {
    path: "candidate",
    label: "Prospects",
    link: `/opportunities/pipeline/100/1/name-asc/_/_/MODIFIERS_ID/candidate/_/100/1/_/_/_`
  },
  leads: {
    label: "Leads",
    path: "sourced",
    link: `/opportunities/pipeline/100/1/name-asc/_/_/MODIFIERS_ID/sourced/_/100/1/_/_/_`
  },
  settings: {
    label: "Settings",
    path: "settings",
    link: "/opportunities/jobs/100/1/name-asc/_/_/MODIFIERS_ID/settings"
  },
  process: {
    label: "Lifecycle Stages",
    path: "jobroleworkflowstep",
    link: "/opportunities/jobs/100/1/name-asc/_/_/MODIFIERS_ID/jobroleworkflowstep/_/100/1/_/_/_"
  },
  runbook: {
    label: "Runbook",
    path: "runbook",
    link: "/opportunities/jobs/100/1/name-asc/_/_/MODIFIERS_ID/runbook"
  }
};

export const JOB_TABS = {
  candidates: {
    path: candidateEntity,
    label: "Candidates",
    link: `/opportunities/pipeline/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/candidate/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [PIPELINE_PERMISSION_LIST]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[PIPELINE_PERMISSION_LIST] ||
      false
  },
  leads: {
    label: "Leads",
    path: "sourced",
    link: `/opportunities/pipeline/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/sourced/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [PIPELINE_PERMISSION_LIST]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[PIPELINE_PERMISSION_LIST] ||
      false
  },
  resumes: {
    label: "Resumes",
    path: "resume",
    link: `/opportunities/pipeline/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/resume/_/100/1/_/_/${JSON.stringify({
      resumeStatus: "new"
    })}`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [PIPELINE_PERMISSION_RESUME]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[PIPELINE_PERMISSION_RESUME] ||
      false
  },
  scores: {
    label: "Scores",
    path: "scorecards",
    link: `/opportunities/pipeline/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/scorecards/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [PIPELINE_PERMISSION_SCORECARDS]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[PIPELINE_PERMISSION_SCORECARDS] ||
      false
  },
  requirements: {
    label: "Requirements",
    path: "requirements",
    link: `/opportunities/jobs/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/requirements/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [JOB_PERMISSION_EVALUATION]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[JOB_PERMISSION_EVALUATION] ||
      false
  },
  planner: {
    label: "Questions",
    path: jobRoleWorkflowQuestionEntity,
    link: `/opportunities/assessments/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/jobroleworkflowquestion/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [JOB_PERMISSION_EVALUATION]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[JOB_PERMISSION_EVALUATION] ||
      false
  },
  rounds: {
    label: "Rounds",
    path: jobRoleWorkflowStepEntity,
    link: `/opportunities/assessments/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/jobroleworkflowstep/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [JOB_PERMISSION_PANEL]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[JOB_PERMISSION_PANEL] ||
      false
  },
  description: {
    label: "Job Description",
    path: jobDescriptionSectionEntity,
    link: `/opportunities/jobs/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/jobpostsection/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [JOB_PERMISSION_DESCRIPTION]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[JOB_PERMISSION_DESCRIPTION] ||
      false
  },
  settings: {
    label: "Settings",
    path: "settings",
    link: `/opportunities/jobs/100/1/name-asc/_/${JSON.stringify({
      hiringStatus: "draft,open,internal"
    })}/MODIFIERS_ID/settings/_/100/1/_/_/_`,
    accessControl: {
      roles: [ACCESS_ROLE_ORG_ADMIN, ACCESS_ROLE_SALES_ORG_ADMIN],
      permissions: [JOB_PERMISSION_FORM]
    },
    showSectionCb: ({
      roles,
      fieldOptions: {
        userJobPermissions: { permissions = {} }
      }
    }) =>
      roles.has(ACCESS_ROLE_ORG_ADMIN) ||
      roles.has(ACCESS_ROLE_SALES_ORG_ADMIN) ||
      permissions[JOB_PERMISSION_FORM] ||
      false
  }
};

export const HIRING_ENGAGEMENT_EVENT_VIEW_SCHEDULING =
  "View Self-Schedule Page";
export const HIRING_ENGAGEMENT_EVENT_VIEW_POST = "View Job Post";
export const HIRING_ENGAGEMENT_EVENT_VIEW_CAREER = "View Career Site";
export const HIRING_ENGAGEMENT_EVENT_TIME_POST = "Time Spent on Job Post";
export const HIRING_ENGAGEMENT_EVENT_VIEW_APP = "View Job Application Page";
export const HIRING_ENGAGEMENT_EVENT_LEAVE_APP = "Leave Job Application Page";

export const ENGAGEMENT_EVENT_VIEW_WEBPAGE = "View Webpage";
export const ENGAGEMENT_EVENT_SCHEDULED_MEETING = "Scheduled Meeting";
export const ENGAGEMENT_EVENT_REQUEST_DOWNLOAD = "Request Download";
export const ENGAGEMENT_EVENT_FORM_SUBMIT = "Form Submit";
export const ENGAGEMENT_EVENT_PIPELINE_ADVANCE = "Pipeline Advance";
export const ENGAGEMENT_EVENT_PIPELINE_ARCHIVE = "Pipeline Archive";
export const ENGAGEMENT_EVENT_RESPOND = "Respond to Email";

export const CADENCE_STEP_TYPES_BY_ID = {
  ...CADENCE_STEP_TYPES.reduce(
    (acc, { value, label, description, Icon }) => ({
      ...acc,
      [value]: { label, description, Icon }
    }),
    {}
  ),
  [ENGAGEMENT_EVENT_RESPOND]: {
    value: ENGAGEMENT_EVENT_RESPOND,
    label: "Response",
    description: "Response from a contact.",
    Icon: PaperAirplaneIcon
  }
};

export const FORM_FIELD_TYPE_TEXT = "text";
export const FORM_FIELD_TYPE_TEXTAREA = "textarea";
export const FORM_FIELD_TYPE_SELECT = "select";
export const FORM_FIELD_TYPE_CHECKBOX = "checkbox";
export const FORM_FIELD_TYPE_RADIO = "radio";
export const FORM_FIELD_TYPE_EMAIL = "email";

export const FORM_FIELD_TYPES = [
  {
    value: FORM_FIELD_TYPE_TEXT,
    label: "Short Answer",
    description: "A single line text input for short responses.",
    Icon: PencilIcon
  },
  {
    value: FORM_FIELD_TYPE_TEXTAREA,
    label: "Long Answer",
    description: "A multi-line text input for longer responses.",
    Icon: DocumentIcon
  },
  {
    value: FORM_FIELD_TYPE_SELECT,
    label: "Select",
    description: "A dropdown menu for selecting one option from a list.",
    Icon: ChevronDoubleDownIcon
  },
  {
    value: FORM_FIELD_TYPE_CHECKBOX,
    label: "Checkbox",
    description: "A box that can be checked or unchecked to select options.",
    Icon: Square2StackIcon
  },
  {
    value: FORM_FIELD_TYPE_RADIO,
    label: "Radio",
    description: "A set of options where only one can be selected at a time.",
    Icon: StopCircleIcon
  },
  {
    value: FORM_FIELD_TYPE_EMAIL,
    label: "Email",
    description: "A field specifically for entering email addresses.",
    Icon: AtSymbolIcon
  }
];

export const FORM_FIELD_TYPES_BY_ID = FORM_FIELD_TYPES.reduce(
  (acc, { value, label, description, Icon }) => ({
    ...acc,
    [value]: { label, description, Icon }
  }),
  {}
);

export const FORM_TRIGGER_TYPE_SUBMITTER = "job_submitter";
export const FORM_TRIGGER_TYPE_REFFERED = "job_referrer";
export const CAMPAIGN_RELATIONSHIP_REFERRED = "referred";
export const CAMPAIGN_RELATIONSHIP_SUBMITTER = "submitter";
